import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ReplayIcon from '@mui/icons-material/Replay';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RepeatIcon from '@mui/icons-material/Repeat';
import EditIcon from '@mui/icons-material/Edit';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';

import DrawStage from './DrawStage';
import CameraSheet from './CameraSheet';
import GivenOutSheet from './GivenOutSheet';
import { raffleApi } from './raffleApi';
import { downloadReceipt, printReceipt } from './rafflePdf';
import {
  Avatar,
  Chip,
  MoneyInput,
  Notice,
  SectionLabel,
  Sheet,
  SheetTitle,
  Spinner,
  TicketStub,
} from './RafflePieces';
import {
  AMBER,
  AMBER_SOFT,
  GOLD,
  GREEN,
  INDIGO,
  LAVENDER_SOFT,
  NAVY,
  PRIZE_PRESETS,
  SLATE,
  colorByName,
  digitsOnly,
  formatPhone,
  fullName,
  money,
  pad,
  shownValue,
} from './raffleTokens';

const TICK_MS = 85;
const SEARCH_DEBOUNCE_MS = 400;

const drawingOf = (run, drawings) =>
  run && run.drawingId
    ? drawings.find((d) => d.drawingId === run.drawingId) || null
    : null;

const booksInPlay = (run, drawings) => {
  const d = drawingOf(run, drawings);
  return ((d && d.books) || []).filter((b) => b.active);
};

const STEPS = [
  { n: 1, label: 'Draw' },
  { n: 2, label: 'Prize' },
  { n: 3, label: 'Pay' },
];

/**
 * The raffle itself — spec §5. Draw → Prize → Pay, no separate review step.
 *
 * The client never decides a number: `raffleApi.draw` returns the server's
 * result and the wheel only animates towards it (R62–R64). Everything that
 * matters is on the server, so a reload or a dropped connection resumes the
 * same raffle (R66) from `openRun`.
 */
export default function DrawFlow({
  mode,
  boot,
  locationId,
  viewOnly,
  notify,
  reloadBoot,
  onModeLock,
}) {
  const settings = boot.settings;
  const drawings = boot.drawings;

  const [run, setRun] = useState(null);
  const [draw, setDraw] = useState(null);
  const [phase, setPhase] = useState('idle');
  const [tick, setTick] = useState(7);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [amount, setAmount] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customAmount, setCustomAmount] = useState('');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [adding, setAdding] = useState(null);

  const [photo, setPhoto] = useState(null); // { blob, url }
  const [cameraOpen, setCameraOpen] = useState(false);

  const [paid, setPaid] = useState(null);
  const [newSeries, setNewSeries] = useState(null);
  const [givenOutOpen, setGivenOutOpen] = useState(false);
  const [carryOpen, setCarryOpen] = useState(false);
  const [seriesOpen, setSeriesOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);

  const timer = useRef(null);
  const searchTimer = useRef(null);

  // Every hook is declared before any derived value. The rules-of-hooks
  // plugin bundled with react-scripts 3.4 (v1.7) counts code paths, so a
  // `&&` / ternary chain above a hook — together with the early returns
  // further down — makes it report that hook as "called conditionally".

  // Let the parent keep the tabs on this raffle's type while it is running.
  useEffect(() => {
    onModeLock(run && run.raffleNo && !paid ? run.mode : null);
  }, [run, paid, onModeLock]);

  useEffect(
    () => () => {
      clearInterval(timer.current);
      clearTimeout(searchTimer.current);
    },
    []
  );

  useEffect(
    () => () => {
      if (photo?.url) URL.revokeObjectURL(photo.url);
    },
    [photo]
  );

  // R92 — an auto number was issued to one customer, so the winner is not a
  // choice to make. Carrying the holder straight onto the pay step means the
  // employee confirms who it is rather than searching for someone, and there
  // is no way to hand the prize to the wrong person (the server uses the
  // entry regardless of what the client sends).
  const holderId = draw?.holder?.customerId;
  useEffect(() => {
    if (!holderId) return;
    setCustomer((c) =>
      c && c.customerId === holderId
        ? c
        : draw.holder.customer || {
            customerId: holderId,
            firstName: draw.holder.customerName,
            lastName: '',
          }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holderId]);

  const resetFlow = useCallback(() => {
    clearInterval(timer.current);
    setRun(null);
    setDraw(null);
    setPhase('idle');
    setStep(1);
    setError('');
    setAmount(null);
    setCustomOpen(false);
    setCustomAmount('');
    setQuery('');
    setResults([]);
    setCustomer(null);
    setAdding(null);
    setPhoto(null);
    setPaid(null);
    setNewSeries(null);
  }, []);

  // R66 — pick up a raffle this employee already has open at this location.
  const adoptRun = useCallback((r) => {
    setRun(r);
    if (r?.pendingDraw) {
      setDraw(r.pendingDraw);
      setPhase('result');
    } else {
      setDraw(null);
      setPhase('idle');
    }
    setStep(1);
  }, []);

  // Only a raffle that already has a number is resumed; one that was started
  // but never drawn is simply started again (the server retires it).
  // This component is keyed by mode, so this runs once per tab.
  useEffect(() => {
    const open = boot.openRun;
    if (open && open.raffleNo && open.mode === mode && open.state === 'open')
      adoptRun(open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Server calls ──────────────────────────────────────────────────────

  const fail = (res) => {
    setBusy(false);
    setError(res?.message || 'Something went wrong.');
    return null;
  };

  const ensureRun = async (drawingId) => {
    if (run) return run;
    const res = await raffleApi.startRun({ locationId, mode, drawingId });
    if (res.code === 'OPEN_RAFFLE' && res.run) {
      notify('info', res.message);
      adoptRun(res.run);
      return null;
    }
    if (!res.success) return fail(res);
    setRun(res.run);
    return res.run;
  };

  const pickDrawing = async (d) => {
    if (busy || viewOnly) return;
    setBusy(true);
    setError('');
    const r = await ensureRun(d.drawingId);
    setBusy(false);
    if (r) setRun(r);
  };

  const changeDrawing = async () => {
    if (run && !run.raffleNo) await raffleApi.closeRun(run.runId);
    resetFlow();
  };

  // Numbers flashed during the spin — presentation only (R64).
  const presentationPool = useMemo(() => {
    if (mode === 'machine') {
      const total = Math.max(1, settings.totalMachines || 1);
      return { pick: () => 1 + Math.floor(Math.random() * total) };
    }
    if (mode === 'auto') {
      // The cycle's issued run, so the blur reads like the real numbers.
      const cycle = drawingOf(run, drawings)?.cycle;
      const lo = cycle?.autoFrom || 1;
      const hi = cycle?.lastIssued || lo;
      return { pick: () => lo + Math.floor(Math.random() * (hi - lo + 1)) };
    }
    const ranges = booksInPlay(run, drawings).filter(
      (b) => b.lastGivenOut >= b.from
    );
    return {
      pick: () => {
        const b = ranges[Math.floor(Math.random() * ranges.length)];
        return b
          ? b.from + Math.floor(Math.random() * (b.lastGivenOut - b.from + 1))
          : 0;
      },
    };
  }, [mode, settings.totalMachines, run, drawings]);

  // ── Derived values (after every hook — see the note above) ────────────
  const live = drawingOf(run, drawings);
  const activeBooks = booksInPlay(run, drawings);
  const discarded = (run && run.discarded) || [];
  const eventUsed = (live && live.eventDraws && live.eventDraws.length) || 0;
  const isAuto = mode === 'auto';
  const eventClosed = Boolean(
    (mode === 'ticket' || isAuto) &&
      live &&
      live.rollover &&
      !draw &&
      eventUsed >= (live.drawsPerEvent || 1)
  );
  // R91 — an auto cycle has no tickets-confirmed gate: nothing was handed
  // out by hand, so there is nothing for the employee to vouch for.
  const needsGate = Boolean(mode === 'ticket' && run && !run.confirmed);
  const winner = draw ? draw.value : undefined;

  // The spin ends on the CLOCK, not on a tick count: browsers slow timers
  // in a background tab to ~1/s, which would turn an 8s spin into minutes.
  // A hidden page skips straight to the result — the number was already
  // saved by the server before any of this started.
  const animateTo = (result) => {
    clearInterval(timer.current);
    setPhase('spin');
    const endsAt = Date.now() + (settings.spinSecs || 5) * 1000;
    timer.current = setInterval(() => {
      if (Date.now() >= endsAt || document.hidden) {
        clearInterval(timer.current);
        setDraw(result);
        setPhase('result');
        return;
      }
      setTick(presentationPool.pick());
    }, TICK_MS);
  };

  const doDraw = async (currentRun) => {
    const r = currentRun || run || (await ensureRun(null));
    if (!r) return;
    setBusy(true);
    setError('');
    const res = await raffleApi.draw(r.runId);
    setBusy(false);
    if (!res.success) {
      setPhase('idle');
      return fail(res);
    }
    setRun(res.run);
    if (res.resumed) {
      setDraw(res.draw);
      setPhase('result');
    } else {
      animateTo(res.draw);
    }
  };

  const confirmGate = async () => {
    setBusy(true);
    const res = await raffleApi.confirmTickets(run.runId);
    setBusy(false);
    if (!res.success) return fail(res);
    setRun(res.run);
  };

  // R25/R26 — one button, no reason picker; the wheel spins again at once.
  const noWinner = async () => {
    if (!draw || busy) return;
    setBusy(true);
    const res = await raffleApi.discardDraw(draw.drawId);
    setBusy(false);
    if (!res.success) return fail(res);
    setDraw(null);
    setRun(res.run);
    if (res.run.rollover) {
      const fresh = await reloadBoot();
      const d = fresh?.drawings?.find((x) => x.drawingId === res.run.drawingId);
      if (d && (d.eventDraws?.length || 0) >= (d.drawsPerEvent || 1)) {
        setPhase('idle');
        return;
      }
    }
    doDraw(res.run);
  };

  const closeUnclaimed = async () => {
    setCloseOpen(false);
    setBusy(true);
    const res = await raffleApi.closeRun(run.runId);
    setBusy(false);
    if (!res.success) return fail(res);
    notify('success', `Raffle #${run.raffleNo} closed — nobody claimed.`);
    await reloadBoot();
    resetFlow();
  };

  const carry = async () => {
    setBusy(true);
    const res = await raffleApi.carryPot(run.runId);
    setBusy(false);
    setCarryOpen(false);
    if (!res.success) return fail(res);
    notify(
      'success',
      `Pot carried: ${money(res.run.potFrom)} → ${money(res.run.potTo)}`
    );
    await reloadBoot();
    resetFlow();
  };

  const pay = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    const res = await raffleApi.pay({
      drawId: draw.drawId,
      amount,
      customerId: customer.customerId,
      photo: photo.blob,
    });
    setBusy(false);
    if (!res.success) return fail(res);
    setPaid({ ...res.raffle, photoBlob: photo.blob });
    setNewSeries(res.newSeries || null);
    reloadBoot();
  };

  // ── Customer search (R32/R33/R37) ────────────────────────────────────

  const onQuery = (v) => {
    setQuery(v);
    clearTimeout(searchTimer.current);
    if (v.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      const res = await raffleApi.searchCustomers(locationId, v.trim());
      setSearching(false);
      setResults(res.success ? res.customers || [] : []);
    }, SEARCH_DEBOUNCE_MS);
  };

  const saveNewCustomer = async () => {
    setBusy(true);
    const res = await raffleApi.addCustomer({ locationId, ...adding });
    setBusy(false);
    if (!res.success) return fail(res);
    setError('');
    setAdding(null);
    setCustomer(res.customer);
  };

  // ── Derived UI ────────────────────────────────────────────────────────

  const canNext =
    (step === 1 && phase === 'result' && !!draw) ||
    (step === 2 && Number(amount) > 0) ||
    (step === 3 && !!customer && !!photo);

  const numberWord =
    mode === 'machine' ? 'Machine' : isAuto ? 'Number' : 'Ticket';
  const winnerLabel =
    mode === 'machine'
      ? `Machine ${winner ? pad(winner) : ''}`
      : `${numberWord} ${winner || ''}`;
  const raffleLabel = run?.raffleNo ? `Raffle #${run.raffleNo}` : 'New raffle';

  if (!boot.onShift && !run) {
    return (
      <div className="rf-pad">
        <Notice Icon={LockIcon}>
          {boot.canManage
            ? 'Raffles are drawn by staff on an open shift. Use History to review them and the gear for Settings and Winners Control.'
            : 'Clock in to run a raffle. Paid raffles are recorded against your open shift.'}
        </Notice>
      </div>
    );
  }
  if (viewOnly) {
    return (
      <div className="rf-pad">
        <Notice Icon={LockIcon}>
          This location is view only for you. You can read History, but draws
          are disabled.
        </Notice>
      </div>
    );
  }

  // ── Success ───────────────────────────────────────────────────────────
  if (paid) {
    return (
      <div className="rf-pad rf-col rf-center-x rf-success">
        <div className="rf-success-badge">
          <CheckIcon style={{ fontSize: 40, color: GREEN }} />
        </div>
        <div className="rf-h2">Raffle #{paid.raffleNo} paid</div>
        <div className="rf-muted" style={{ marginBottom: 20 }}>
          {money(paid.amount)} to {paid.customerName}
        </div>

        <div className="rf-card rf-w100">
          {[
            ['Raffle', `#${paid.raffleNo} · ${paid.date}`],
            [
              numberWord,
              mode === 'machine'
                ? pad(paid.value)
                : isAuto
                ? String(paid.value)
                : `${paid.value} · ${paid.colorName}`,
            ],
            ...(mode === 'ticket' || isAuto
              ? [['Drawing', paid.drawingName]]
              : []),
            ...(paid.poolText
              ? [
                  [
                    isAuto ? 'Numbers in the draw' : 'Tickets in the draw',
                    paid.poolText,
                  ],
                ]
              : []),
            ['Prize', money(paid.amount)],
            ['Customer', paid.customerName],
            ['Paid by', `${paid.byName} · ${paid.time}`],
          ].map(([k, v]) => (
            <div key={k} className="rf-kv">
              <span>{k}</span>
              <strong>{v}</strong>
            </div>
          ))}
          <div className="rf-row rf-gap-8" style={{ marginTop: 16 }}>
            <button
              type="button"
              className="rf-btn rf-btn-navy rf-flex1"
              onClick={() => downloadReceipt(paid, boot.locationName)}
            >
              <DownloadIcon fontSize="small" /> Download PDF
            </button>
            <button
              type="button"
              className="rf-btn rf-btn-outline rf-flex1"
              onClick={() => printReceipt(paid, boot.locationName)}
            >
              <PrintIcon fontSize="small" /> Print
            </button>
          </div>
        </div>

        {newSeries ? (
          <button
            type="button"
            className="rf-btn rf-btn-amber rf-w100"
            onClick={() => setSeriesOpen(true)}
          >
            Rollover won — start a new {isAuto ? 'cycle' : 'ticket series'}
          </button>
        ) : (
          <button
            type="button"
            className="rf-btn rf-btn-lavender rf-w100"
            onClick={resetFlow}
          >
            Start the next raffle
          </button>
        )}

        {seriesOpen && newSeries && (
          <SeriesSheet
            drawing={drawings.find((d) => d.drawingId === newSeries.drawingId)}
            defaultFrom={newSeries.from}
            locationId={locationId}
            onClose={() => setSeriesOpen(false)}
            onSaved={async () => {
              setSeriesOpen(false);
              notify('success', 'New ticket series saved.');
              await reloadBoot();
              resetFlow();
            }}
          />
        )}
      </div>
    );
  }

  // ── Step content ──────────────────────────────────────────────────────

  // Each tab picks from its own drawings: a paper-book drawing cannot be run
  // as an auto raffle, and the server refuses the mismatch either way.
  const modeDrawings = drawings.filter((d) => (isAuto ? d.auto : !d.auto));

  const drawingPicker = (mode === 'ticket' || isAuto) && !run && (
    <>
      <div className="rf-strong">Which drawing are you running?</div>
      {modeDrawings.length === 0 && (
        <Notice Icon={ConfirmationNumberIcon}>
          {isAuto
            ? 'No auto-number raffle is set up yet. A manager adds one in Raffle Settings, with its numbers set to "NuVe auto numbers".'
            : 'No drawings are set up yet. A manager adds them in Raffle Settings.'}
        </Notice>
      )}
      {modeDrawings.map((d) => {
        const books = d.books.filter((b) => b.active);
        const issued = d.cycle ? d.cycle.issuedCount : 0;
        const usable = d.auto
          ? issued > 0 && !d.seriesClosed
          : books.length > 0 && !d.seriesClosed;
        return (
          <button
            key={d.drawingId}
            type="button"
            className="rf-card rf-pick"
            style={{ opacity: usable ? 1 : 0.55 }}
            disabled={!usable || busy}
            onClick={() => pickDrawing(d)}
          >
            <div className="rf-pick-icon">
              {d.rollover ? (
                <RepeatIcon style={{ color: INDIGO }} />
              ) : (
                <ConfirmationNumberIcon style={{ color: INDIGO }} />
              )}
            </div>
            <div className="rf-flex1 rf-minw0">
              <div className="rf-strong">{d.name}</div>
              {d.auto ? (
                <div
                  className="rf-col rf-gap-8"
                  style={{ marginTop: 8, alignItems: 'flex-start' }}
                >
                  <span className="rf-xs rf-muted">
                    {issued > 0
                      ? `Numbers ${d.cycle.autoFrom}–${
                          d.cycle.lastIssued
                        } · ${issued} earned by ${
                          d.cycle.customerCount
                        } customer${d.cycle.customerCount === 1 ? '' : 's'}`
                      : 'No numbers earned in this cycle yet'}
                  </span>
                  {d.seriesClosed && (
                    <span className="rf-amber-text">
                      Rollover won — waiting for a new cycle
                    </span>
                  )}
                </div>
              ) : (
                <div
                  className="rf-col rf-gap-8"
                  style={{ marginTop: 8, alignItems: 'flex-start' }}
                >
                  {books.map((b) => (
                    <TicketStub
                      key={b.bookId}
                      number={
                        b.lastGivenOut >= b.from
                          ? `${b.from}–${b.lastGivenOut}`
                          : `${b.from} · none out`
                      }
                      color={b.colorName}
                      size="sm"
                    />
                  ))}
                  {!books.length && (
                    <span className="rf-amber-text">No active books</span>
                  )}
                  {d.seriesClosed && (
                    <span className="rf-amber-text">
                      Rollover won — waiting for a new ticket series
                    </span>
                  )}
                </div>
              )}
              {d.rollover && (
                <div className="rf-amber-text" style={{ marginTop: 6 }}>
                  Pot {money(d.pot)} · +{money(d.potIncrease)} if nobody wins ·{' '}
                  {d.eventDraws.length}/{d.drawsPerEvent} numbers drawn
                </div>
              )}
            </div>
            <ChevronRightIcon style={{ color: SLATE }} />
          </button>
        );
      })}
    </>
  );

  const drawingHeader = (mode === 'ticket' || isAuto) && run && live && (
    <div className="rf-card">
      <div className="rf-row rf-between">
        <div>
          <div className="rf-xs rf-muted">Drawing</div>
          <div className="rf-strong">{live.name}</div>
        </div>
        {phase === 'idle' && !run.raffleNo && (
          <button type="button" className="rf-pill-btn" onClick={changeDrawing}>
            Change
          </button>
        )}
      </div>
      {/* No "fix the tickets that went out" row for an auto cycle — there is
          nothing to correct. What IS in the draw is shown instead. */}
      {isAuto && live.cycle && (
        <div className="rf-xs rf-muted" style={{ marginTop: 10 }}>
          {live.cycle.lastIssued
            ? `Numbers ${live.cycle.autoFrom}–${
                live.cycle.lastIssued
              } in the draw · ${live.cycle.issuedCount} earned by ${
                live.cycle.customerCount
              } customer${live.cycle.customerCount === 1 ? '' : 's'}${
                live.cycle.startedOn ? ` · since ${live.cycle.startedOn}` : ''
              }`
            : 'No numbers earned in this cycle yet.'}
        </div>
      )}
      {!isAuto && !needsGate && phase === 'idle' && (
        // R81 — the stubs stay visible after confirming, with a Fix action.
        <button
          type="button"
          className="rf-fix-row"
          onClick={() => setGivenOutOpen(true)}
        >
          <div className="rf-row rf-wrap rf-gap-8">
            {activeBooks.map((b) => (
              <TicketStub
                key={b.bookId}
                number={
                  b.lastGivenOut >= b.from
                    ? `${b.from}–${b.lastGivenOut}`
                    : 'none out'
                }
                color={b.colorName}
                size="sm"
              />
            ))}
          </div>
          <span className="rf-row rf-gap-4 rf-indigo-text">
            <EditIcon style={{ fontSize: 14 }} /> Fix
          </span>
        </button>
      )}
      {live.rollover && (
        <div className="rf-row rf-wrap rf-gap-12 rf-divider-top">
          <span className="rf-badge-amber">
            <RepeatIcon style={{ fontSize: 14 }} /> Pot {money(live.pot)}
          </span>
          <span className="rf-xs rf-strong-slate">
            Number {Math.min(eventUsed + 1, live.drawsPerEvent)} of{' '}
            {live.drawsPerEvent}
          </span>
          {eventUsed > 0 && (
            <span className="rf-xs rf-muted">
              Already out: {live.eventDraws.map((e) => e.value).join(', ')}
            </span>
          )}
        </div>
      )}
    </div>
  );

  // R77–R79 — the wheel stays hidden until this is answered.
  const gate = needsGate && phase === 'idle' && live && (
    <div className="rf-card rf-gate">
      <div className="rf-row rf-gap-8">
        <ConfirmationNumberIcon style={{ fontSize: 18, color: INDIGO }} />
        <span className="rf-h3">Are these the tickets that went out?</span>
      </div>
      <div className="rf-xs rf-muted" style={{ margin: '4px 0 12px' }}>
        The draw only picks from tickets that were actually handed out. Check
        the last number given for each colour.
      </div>
      <div className="rf-col rf-gap-8" style={{ marginBottom: 16 }}>
        {activeBooks.map((b) => (
          <div key={b.bookId} className="rf-book-row">
            <TicketStub
              number={
                b.lastGivenOut >= b.from
                  ? `${b.from}–${b.lastGivenOut}`
                  : 'none out'
              }
              color={b.colorName}
              size="sm"
              notchBg="#f6f7fb"
            />
            <div className="rf-flex1 rf-minw0">
              <div className="rf-xs rf-strong">{b.givenOut} given out</div>
              <div className="rf-xs rf-muted">Book goes to {b.to}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="rf-row rf-gap-8">
        <button
          type="button"
          className="rf-btn rf-btn-outline rf-flex1"
          onClick={() => setGivenOutOpen(true)}
        >
          <EditIcon fontSize="small" /> Update numbers
        </button>
        <button
          type="button"
          className="rf-btn rf-btn-navy rf-flex1"
          disabled={busy}
          onClick={confirmGate}
        >
          <CheckIcon fontSize="small" /> Yes, correct
        </button>
      </div>
    </div>
  );

  const carryCard = eventClosed && phase === 'idle' && (
    <div className="rf-card">
      <div className="rf-row rf-gap-8" style={{ marginBottom: 8 }}>
        <RepeatIcon style={{ color: AMBER }} />
        <span className="rf-h3">No winner this time</span>
      </div>
      <div className="rf-muted" style={{ marginBottom: 16 }}>
        {live.drawsPerEvent} number{live.drawsPerEvent === 1 ? '' : 's'} came
        out and nobody claimed. The pot carries to the next drawing
        {isAuto
          ? ', every number already earned stays valid, and customers keep earning more on each visit.'
          : ', the same tickets stay valid, and the location keeps handing out the same series.'}
      </div>
      <button
        type="button"
        className="rf-btn rf-btn-navy rf-w100"
        onClick={() => setCarryOpen(true)}
      >
        Carry pot to next drawing
      </button>
    </div>
  );

  // Phones: header, wheel, controls stacked. Wide screens: the wheel on the
  // left, header and controls on the right (see .rf-draw-grid).
  const stepOne = (
    <div className="rf-col rf-gap-16">
      {drawingPicker && <div className="rf-pick-grid">{drawingPicker}</div>}
      {(mode === 'machine' || run) && (
        <div className={`rf-draw-grid ${drawingHeader ? '' : 'rf-no-head'}`}>
          {drawingHeader && <div className="rf-area-head">{drawingHeader}</div>}
          <div className="rf-area-stage">
            {gate}
            {!gate && carryCard}
            {!gate && !carryCard && (
              <DrawStage
                phase={phase}
                value={winner}
                tick={tick}
                mode={mode}
                accent={
                  mode === 'ticket'
                    ? colorByName(
                        draw?.colorName || activeBooks[0]?.colorName || 'Yellow'
                      ).hex
                    : GOLD
                }
              />
            )}
          </div>
          {!gate && !carryCard && (
            <div className="rf-area-side rf-col rf-gap-16">
              {discarded.length > 0 && (
                <div className="rf-card">
                  <div
                    className="rf-row rf-between"
                    style={{ marginBottom: 10 }}
                  >
                    <span className="rf-section-label" style={{ margin: 0 }}>
                      Numbers already out
                    </span>
                    <span className="rf-xs rf-strong-slate">
                      {discarded.length} with no winner
                    </span>
                  </div>
                  <div className="rf-row rf-wrap rf-gap-8">
                    {discarded.map((d, i) =>
                      d.colorName ? (
                        <div key={i} style={{ opacity: 0.5 }}>
                          <TicketStub
                            number={d.value}
                            color={d.colorName}
                            size="sm"
                          />
                        </div>
                      ) : (
                        <span key={i} className="rf-out-number">
                          {pad(d.value)}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}

              {phase === 'idle' && (
                <button
                  type="button"
                  className="rf-btn rf-btn-draw"
                  disabled={busy}
                  onClick={() => doDraw()}
                >
                  {busy ? (
                    <Spinner />
                  ) : (
                    <BoltIcon style={{ color: GOLD, fontSize: 24 }} />
                  )}
                  {mode === 'machine'
                    ? 'DRAW MACHINE'
                    : isAuto
                    ? 'DRAW NUMBER'
                    : 'DRAW TICKET'}
                </button>
              )}

              {phase === 'result' && draw && (
                <>
                  <div className="rf-card">
                    <div
                      className="rf-row rf-between"
                      style={{ marginBottom: 12 }}
                    >
                      <span className="rf-section-label" style={{ margin: 0 }}>
                        Draw record
                      </span>
                      <span className="rf-strong rf-indigo-text">
                        {raffleLabel}
                      </span>
                    </div>
                    <div className="rf-grid3">
                      <div>
                        <div className="rf-xs rf-muted">{numberWord}</div>
                        {mode === 'ticket' ? (
                          <TicketStub
                            number={winner}
                            color={draw.colorName}
                            size="sm"
                          />
                        ) : (
                          <span className="rf-strong">
                            {isAuto ? winner : pad(winner)}
                          </span>
                        )}
                      </div>
                      <div>
                        <div className="rf-xs rf-muted">Drawn at</div>
                        <div className="rf-strong">{draw.time}</div>
                      </div>
                      <div>
                        <div className="rf-xs rf-muted">By</div>
                        <div className="rf-strong">{draw.byName}</div>
                      </div>
                    </div>
                    {/* R92 — an auto number already names its holder, so the
                        draw record says who won before the pay step opens. */}
                    {draw.holder && (
                      <div className="rf-holder-row" style={{ marginTop: 12 }}>
                        <Avatar
                          name={draw.holder.customerName}
                          size={36}
                          src={draw.holder.customer?.photoUrl}
                        />
                        <div className="rf-flex1 rf-minw0">
                          <div className="rf-strong">
                            {draw.holder.customerName}
                          </div>
                          <div className="rf-xs rf-muted">
                            Earned {draw.holder.date}, {draw.holder.time}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="rf-xs rf-muted" style={{ marginTop: 8 }}>
                      Out of {draw.poolSize} eligible{' '}
                      {mode === 'machine'
                        ? 'machines'
                        : isAuto
                        ? 'numbers'
                        : 'tickets'}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="rf-btn rf-btn-outline rf-w100"
                    disabled={busy}
                    onClick={noWinner}
                  >
                    <ReplayIcon fontSize="small" /> No winner — draw again
                  </button>
                </>
              )}

              {run?.raffleNo && phase !== 'spin' && (
                // Ending a raffle with nobody paid is the one negative
                // outcome on this screen, and it used to be a 12px grey
                // underline that read as a footnote. Red and full size, so
                // it is found when it is needed and not clicked by accident.
                <button
                  type="button"
                  className="rf-btn rf-btn-unclaimed rf-w100"
                  onClick={() => setCloseOpen(true)}
                >
                  <DoNotDisturbIcon style={{ fontSize: 18 }} /> Close{' '}
                  {raffleLabel} — nobody claimed
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const stepTwo = (
    <div className="rf-col rf-gap-16">
      <div className="rf-card">
        <div className="rf-row rf-between" style={{ marginBottom: 16 }}>
          <SectionLabel style={{ margin: 0 }}>Select prize</SectionLabel>
          {/* R31 — the number is context here, never an input. */}
          <span className="rf-lock-chip">
            <LockIcon style={{ fontSize: 13 }} /> {winnerLabel}
          </span>
        </div>

        {(mode === 'ticket' || isAuto) && live?.rollover && (
          <button
            type="button"
            className="rf-pot-option"
            onClick={() => {
              setAmount(live.pot);
              setCustomOpen(false);
            }}
            data-on={Number(amount) === live.pot ? '1' : '0'}
          >
            <span className="rf-row rf-gap-8">
              <RepeatIcon style={{ fontSize: 17 }} /> Rollover pot
            </span>
            <span className="rf-pot-amount">{money(live.pot)}</span>
          </button>
        )}

        <div className="rf-prize-grid">
          {PRIZE_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              className="rf-prize"
              data-on={Number(amount) === p && !customOpen ? '1' : '0'}
              onClick={() => {
                setAmount(p);
                setCustomOpen(false);
                setCustomAmount('');
              }}
            >
              ${p}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="rf-dashed-btn"
          style={{
            background: customOpen ? LAVENDER_SOFT : '#fff',
            marginTop: 12,
          }}
          onClick={() => setCustomOpen(true)}
        >
          <AddIcon fontSize="small" /> Other amount
        </button>
        {customOpen && (
          <div style={{ marginTop: 12 }}>
            <MoneyInput
              big
              value={customAmount}
              onChange={(v) => {
                setCustomAmount(v);
                setAmount(v ? Number(v) : null);
              }}
            />
          </div>
        )}
      </div>

      {Number(amount) > 0 && (
        <div className="rf-prize-summary">
          <span>Prize for {winnerLabel.toLowerCase()}</span>
          <strong>{money(amount)}</strong>
        </div>
      )}
    </div>
  );

  const stepThree = (
    <div className="rf-col rf-gap-16">
      {!customer && !adding && (
        <>
          <div className="rf-card">
            <div className="rf-search">
              <SearchIcon style={{ color: SLATE }} />
              <input
                value={query}
                onChange={(e) => onQuery(e.target.value)}
                placeholder="Name, phone or customer ID"
                autoFocus
              />
              {searching && <Spinner size={16} color={INDIGO} />}
              {query && (
                <button
                  type="button"
                  className="rf-icon-btn"
                  onClick={() => onQuery('')}
                  aria-label="Clear"
                >
                  <CloseIcon style={{ color: SLATE, fontSize: 18 }} />
                </button>
              )}
            </div>
            {!query && (
              <div className="rf-xs rf-muted" style={{ marginTop: 12 }}>
                {mode === 'machine'
                  ? `Search the customer who was playing machine ${pad(
                      winner
                    )}.`
                  : `Search the customer holding ticket ${winner}.`}
              </div>
            )}
          </div>

          <div className="rf-results-grid">
            {results.map((c) => (
              <button
                key={c.customerId}
                type="button"
                className="rf-card rf-pick"
                onClick={() => setCustomer(c)}
              >
                <Avatar name={fullName(c)} src={c.photoUrl} />
                <div className="rf-flex1 rf-minw0">
                  <div className="rf-strong">{fullName(c)}</div>
                  <div className="rf-xs rf-muted">{formatPhone(c.phone)}</div>
                  <div className="rf-xs rf-muted">
                    Last visit: {c.lastVisit || '—'}
                  </div>
                </div>
                {c.recentWins > 0 && (
                  <span
                    className={
                      c.recentWins >= 3 ? 'rf-badge-amber' : 'rf-badge-lav'
                    }
                  >
                    <EmojiEventsIcon style={{ fontSize: 13 }} /> {c.recentWins}
                  </span>
                )}
                <ChevronRightIcon style={{ color: SLATE }} />
              </button>
            ))}
            {query.trim().length >= 2 && !searching && results.length === 0 && (
              <div className="rf-card rf-empty">
                <div className="rf-strong">No customer matches “{query}”</div>
                <div
                  className="rf-xs rf-muted"
                  style={{ margin: '4px 0 16px' }}
                >
                  Check the phone number, or register the customer now.
                </div>
                <button
                  type="button"
                  className="rf-pill-btn"
                  onClick={() => {
                    const digits = query.replace(/\D/g, '');
                    const words = query
                      .replace(/[0-9]/g, '')
                      .trim()
                      .split(/\s+/);
                    setAdding({
                      firstName: digits ? '' : words[0] || '',
                      lastName: digits ? '' : words.slice(1).join(' '),
                      phone: digits.slice(0, 10),
                    });
                  }}
                >
                  <AddIcon style={{ fontSize: 16 }} /> Add new customer
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {adding && (
        <div className="rf-card rf-col rf-gap-12">
          <div className="rf-h3">Add new customer</div>
          <div className="rf-row rf-gap-8">
            <input
              className="rf-input rf-flex1"
              placeholder="First name"
              value={adding.firstName}
              onChange={(e) => {
                const firstName = e.target.value;
                setAdding((a) => ({ ...a, firstName }));
              }}
            />
            <input
              className="rf-input rf-flex1"
              placeholder="Last name"
              value={adding.lastName}
              onChange={(e) => {
                const lastName = e.target.value;
                setAdding((a) => ({ ...a, lastName }));
              }}
            />
          </div>
          <input
            className="rf-input"
            placeholder="Phone (10 digits)"
            inputMode="numeric"
            value={adding.phone}
            onChange={(e) => {
              const phone = digitsOnly(e.target.value, 10);
              setAdding((a) => ({ ...a, phone }));
            }}
          />
          <div className="rf-row rf-gap-8">
            <button
              type="button"
              className="rf-btn rf-btn-outline rf-flex1"
              onClick={() => setAdding(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rf-btn rf-btn-navy rf-flex1"
              disabled={
                busy ||
                !adding.firstName.trim() ||
                !adding.lastName.trim() ||
                adding.phone.length !== 10
              }
              onClick={saveNewCustomer}
            >
              Add customer
            </button>
          </div>
        </div>
      )}

      {customer && (
        <div className="rf-pay-grid">
          <div className="rf-card rf-nopad">
            <div className="rf-customer-hero">
              <Avatar
                name={fullName(customer)}
                size={104}
                ring={!!photo}
                src={photo?.url || customer.photoUrl}
              />
              <div className="rf-h2" style={{ marginTop: 12 }}>
                {fullName(customer)}
              </div>
              {customer.memberSince && (
                <div className="rf-xs rf-muted">
                  Member since {String(customer.memberSince).split(' ')[0]}
                </div>
              )}
              {/* Why this customer and nobody else: the number was theirs. */}
              {isAuto && draw?.holder && (
                <span className="rf-lock-chip" style={{ marginTop: 10 }}>
                  <LockIcon style={{ fontSize: 13 }} /> Earned #{winner} on{' '}
                  {draw.holder.date}, {draw.holder.time}
                </span>
              )}
              {/* R37/R61 — the same count Winners Control shows. */}
              <span
                className={
                  customer.recentWins >= 3 ? 'rf-badge-amber' : 'rf-badge-lav'
                }
                style={{ marginTop: 12 }}
              >
                <EmojiEventsIcon style={{ fontSize: 14 }} />
                {customer.recentWins === 0
                  ? `No wins in the last ${boot.winWindowDays / 7} weeks`
                  : `Won ${customer.recentWins} raffle${
                      customer.recentWins > 1 ? 's' : ''
                    } in the last ${boot.winWindowDays / 7} weeks`}
              </span>
              {customer.isBlocked && (
                <span className="rf-badge-red" style={{ marginTop: 8 }}>
                  <WarningAmberIcon style={{ fontSize: 14 }} /> Customer is
                  blocked
                </span>
              )}
            </div>
            <div
              className="rf-grid3 rf-divider-top"
              style={{ padding: '12px 8px' }}
            >
              {[
                ['Phone', formatPhone(customer.phone) || '—'],
                ['Last visit', String(customer.lastVisit || '—').split(' ')[0]],
                ['Wins', customer.recentWins],
              ].map(([k, v]) => (
                <div key={k} className="rf-text-center">
                  <div className="rf-xs rf-muted">{k}</div>
                  <div className="rf-xs rf-strong">{v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rf-col rf-gap-16">
            <button
              type="button"
              className="rf-photo-btn"
              data-done={photo ? '1' : '0'}
              onClick={() => setCameraOpen(true)}
            >
              <span className="rf-photo-icon">
                {photo ? (
                  <CheckIcon style={{ color: '#fff', fontSize: 24 }} />
                ) : (
                  <PhotoCameraIcon style={{ color: GOLD, fontSize: 24 }} />
                )}
              </span>
              <span className="rf-flex1 rf-text-left">
                <span className="rf-photo-title">
                  {photo ? 'Photo taken' : 'Take customer photo'}
                </span>
                <span className="rf-photo-sub">
                  {photo
                    ? 'Tap to retake before paying'
                    : 'Required before paying the prize'}
                </span>
              </span>
              {!photo && <ArrowForwardIcon style={{ color: '#fff' }} />}
            </button>

            {photo && (
              // R36 — the review sits inline on the Pay step; there is no 4th step.
              <div className="rf-card rf-nopad">
                <div className="rf-paying-head">
                  <span>Paying now</span>
                  <span style={{ color: GOLD }}>{raffleLabel}</span>
                </div>
                <div className="rf-row rf-between" style={{ padding: 16 }}>
                  <div>
                    <div className="rf-xs rf-muted" style={{ marginBottom: 4 }}>
                      {numberWord}
                    </div>
                    {mode === 'ticket' ? (
                      <TicketStub
                        number={winner}
                        color={draw?.colorName}
                        size="md"
                      />
                    ) : (
                      <span className="rf-big-number">
                        {isAuto ? winner : pad(winner)}
                      </span>
                    )}
                  </div>
                  <div className="rf-text-right">
                    <div className="rf-xs rf-muted" style={{ marginBottom: 4 }}>
                      Prize
                    </div>
                    <span className="rf-big-number" style={{ color: GREEN }}>
                      {money(amount)}
                    </span>
                  </div>
                </div>
                <div className="rf-paying-foot">
                  <CheckCircleIcon style={{ fontSize: 15, color: GREEN }} />
                  <span>
                    {fullName(customer)} · photo taken now · {boot.userName}
                  </span>
                </div>
              </div>
            )}

            {/* R92 — on an auto draw the winner is fixed by the number, so
                there is no "different customer" to choose. Getting it wrong
                is a void, not a swap. */}
            {!isAuto && (
              <button
                type="button"
                className="rf-btn rf-btn-outline rf-w100"
                onClick={() => {
                  setCustomer(null);
                  setPhoto(null);
                }}
              >
                Choose a different customer
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // On the Draw step the bar only appears once a number is out — before that
  // there is nothing to continue to, and on a phone it would sit on top of
  // the DRAW button.
  const showActionBar =
    (mode === 'machine' || run) &&
    !eventClosed &&
    !needsGate &&
    (step > 1 || phase === 'result');

  return (
    <>
      <div className="rf-pad" style={{ paddingBottom: 12 }}>
        <div className="rf-steps">
          {STEPS.map((s, i) => {
            const done = step > s.n;
            const active = step === s.n;
            return (
              <React.Fragment key={s.n}>
                <div className="rf-step">
                  <span
                    className="rf-step-dot"
                    data-state={done ? 'done' : active ? 'active' : ''}
                  >
                    {done ? <CheckIcon style={{ fontSize: 15 }} /> : s.n}
                  </span>
                  <span
                    className="rf-step-label"
                    style={{ color: active ? NAVY : SLATE }}
                  >
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <span
                    className="rf-step-line"
                    data-done={step > s.n ? '1' : '0'}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="rf-chips">
          <Chip label={raffleLabel} active />
          {(mode === 'ticket' || isAuto) && live && (
            <Chip label={live.name} done={step > 1} />
          )}
          <Chip
            label={numberWord}
            value={winner ? shownValue(mode, winner) : '—'}
            done={!!winner && step > 1}
          />
          <Chip
            label="Prize"
            value={amount ? money(amount) : '—'}
            done={!!amount && step > 2}
          />
          <Chip
            label="Customer"
            value={customer ? customer.firstName : '—'}
            done={!!customer && !!photo}
          />
        </div>
      </div>

      <div className="rf-pad rf-pad-top0">
        {error && (
          <div style={{ marginBottom: 12 }}>
            <Notice Icon={WarningAmberIcon} tone="red">
              {error}
            </Notice>
          </div>
        )}
        {step === 1 && stepOne}
        {step === 2 && stepTwo}
        {step === 3 && stepThree}
      </div>

      {showActionBar && (
        <div className="rf-actionbar">
          <div className="rf-row rf-gap-8 rf-actionbar-row">
            {step > 1 && (
              <button
                type="button"
                className="rf-btn rf-btn-outline"
                onClick={() => setStep(step - 1)}
              >
                <ArrowBackIcon fontSize="small" /> Back
              </button>
            )}
            <button
              type="button"
              className="rf-btn rf-flex1 rf-btn-next"
              data-kind={canNext ? (step === 3 ? 'pay' : 'next') : 'off'}
              disabled={!canNext || busy}
              onClick={() => (step === 3 ? pay() : setStep(step + 1))}
            >
              {busy && step === 3 && <Spinner />}
              {step === 1
                ? 'Select prize'
                : step === 2
                ? // On an auto raffle there is nobody to find — the number
                  // already names its winner; the step confirms them.
                  isAuto
                  ? 'Confirm winner'
                  : 'Find customer'
                : `Pay ${money(amount)} & finish`}
              {step < 3 ? (
                <ArrowForwardIcon fontSize="small" />
              ) : (
                !busy && <CheckIcon fontSize="small" />
              )}
            </button>
          </div>
          {!canNext && (
            <div className="rf-actionbar-hint">
              {step === 1 &&
                (phase === 'spin'
                  ? 'Drawing…'
                  : mode === 'machine'
                  ? 'Draw a machine to continue'
                  : isAuto
                  ? 'Draw a number to continue'
                  : 'Draw a ticket to continue')}
              {step === 2 && 'Select the prize amount'}
              {step === 3 &&
                (!customer
                  ? 'Select the winning customer'
                  : "Take the customer's photo to pay")}
            </div>
          )}
        </div>
      )}

      {cameraOpen && customer && (
        <CameraSheet
          customerName={fullName(customer)}
          locationId={locationId}
          onClose={() => setCameraOpen(false)}
          onCapture={(blob) => {
            setPhoto({ blob, url: URL.createObjectURL(blob) });
            setCameraOpen(false);
          }}
        />
      )}

      {givenOutOpen && live && (
        <GivenOutSheet
          drawing={live}
          locationId={locationId}
          runId={run?.runId}
          onClose={() => setGivenOutOpen(false)}
          onSaved={async (res) => {
            setGivenOutOpen(false);
            if (res.run) setRun(res.run);
            await reloadBoot();
          }}
        />
      )}

      {carryOpen && live && (
        <Sheet onClose={() => setCarryOpen(false)}>
          <SheetTitle Icon={RepeatIcon} iconColor={AMBER}>
            Carry to the next drawing
          </SheetTitle>
          <div className="rf-muted" style={{ marginBottom: 16 }}>
            The drawing ran and nobody won, so the pot goes up.{' '}
            {isAuto
              ? `Numbers ${
                  live.cycle && live.cycle.lastIssued
                    ? `${live.cycle.autoFrom}–${live.cycle.lastIssued}`
                    : ''
                } stay valid and customers keep earning more on each visit.`
              : `Tickets ${activeBooks
                  .map((b) => `${b.from}–${b.to}`)
                  .join(
                    ', '
                  )} stay valid and the location keeps handing out the same series.`}
          </div>
          {/* R42 — the arithmetic is shown; nobody types the amount. */}
          <div className="rf-carry-table">
            <div className="rf-row rf-between">
              <span>Pot this drawing</span>
              <strong style={{ color: SLATE }}>{money(live.pot)}</strong>
            </div>
            <div className="rf-row rf-between">
              <span>Increase</span>
              <strong style={{ color: AMBER }}>
                + {money(live.potIncrease)}
              </strong>
            </div>
            <div
              className="rf-row rf-between"
              style={{ background: AMBER_SOFT }}
            >
              <span style={{ color: AMBER, fontWeight: 600 }}>
                Next drawing
              </span>
              <strong className="rf-big-number" style={{ color: AMBER }}>
                {money((live.pot || 0) + (live.potIncrease || 0))}
              </strong>
            </div>
          </div>
          {eventUsed > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Notice tone="slate">
                Saved to the record — numbers drawn with no winner:{' '}
                {live.eventDraws.map((e) => e.value).join(', ')}
              </Notice>
            </div>
          )}
          <div className="rf-row rf-gap-8">
            <button
              type="button"
              className="rf-btn rf-btn-outline rf-flex1"
              onClick={() => setCarryOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rf-btn rf-btn-navy rf-flex1"
              disabled={busy}
              onClick={carry}
            >
              Confirm carry over
            </button>
          </div>
        </Sheet>
      )}

      {closeOpen && (
        <Sheet onClose={() => setCloseOpen(false)}>
          <SheetTitle
            Icon={DoNotDisturbIcon}
            iconColor={SLATE}
            onClose={() => setCloseOpen(false)}
          >
            Close {raffleLabel}?
          </SheetTitle>
          <div className="rf-muted" style={{ marginBottom: 16 }}>
            Use this when nobody claimed the prize and you are not drawing
            again. The raffle stays in History as Unclaimed
            {draw
              ? `, with ${shownValue(mode, draw.value)} as the last number out`
              : ''}
            .
          </div>
          <div className="rf-row rf-gap-8">
            <button
              type="button"
              className="rf-btn rf-btn-outline rf-flex1"
              onClick={() => setCloseOpen(false)}
            >
              Keep it open
            </button>
            <button
              type="button"
              className="rf-btn rf-btn-navy rf-flex1"
              disabled={busy}
              onClick={closeUnclaimed}
            >
              Close — nobody claimed
            </button>
          </div>
        </Sheet>
      )}
    </>
  );
}

/**
 * R45 — the next series: from (pre-filled), to, and the starting pot.
 *
 * An auto cycle has no "to": nothing is printed ahead of time, so it simply
 * counts up from where it starts for as long as the cycle runs.
 */
export function SeriesSheet({
  drawing,
  defaultFrom,
  locationId,
  onClose,
  onSaved,
}) {
  const auto = !!drawing?.auto;
  const [from, setFrom] = useState(String(defaultFrom || ''));
  const [to, setTo] = useState(
    defaultFrom ? String(Number(defaultFrom) + 499) : ''
  );
  const [pot, setPot] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setBusy(true);
    const res = await raffleApi.startNewSeries({
      locationId,
      drawingId: drawing?.drawingId,
      from,
      to,
      pot: pot || 0,
    });
    setBusy(false);
    if (!res.success) return setError(res.message);
    onSaved(res);
  };

  return (
    <Sheet onClose={onClose}>
      <SheetTitle Icon={ConfirmationNumberIcon} onClose={onClose}>
        Start a new {auto ? 'cycle' : 'ticket series'}
      </SheetTitle>
      <div className="rf-muted" style={{ marginBottom: 16 }}>
        {auto
          ? 'The rollover was won, so that cycle is closed. Every number it issued stays on file; the next qualifying visit starts the new cycle here.'
          : 'The rollover was won, so the old tickets are closed. Enter the numbers the location will start handing out.'}
      </div>
      <div className="rf-row rf-gap-8" style={{ marginBottom: 16 }}>
        <div className="rf-flex1">
          <SectionLabel>{auto ? 'Start numbering at' : 'From'}</SectionLabel>
          <input
            className="rf-input"
            inputMode="numeric"
            value={from}
            onChange={(e) => setFrom(digitsOnly(e.target.value))}
          />
        </div>
        {!auto && (
          <div className="rf-flex1">
            <SectionLabel>To</SectionLabel>
            <input
              className="rf-input"
              inputMode="numeric"
              value={to}
              onChange={(e) => setTo(digitsOnly(e.target.value))}
            />
          </div>
        )}
      </div>
      <SectionLabel>Starting pot</SectionLabel>
      <MoneyInput big value={pot} onChange={setPot} />
      {error && (
        <div style={{ marginTop: 12 }}>
          <Notice tone="red">{error}</Notice>
        </div>
      )}
      <button
        type="button"
        className="rf-btn rf-btn-navy rf-w100"
        style={{ marginTop: 20 }}
        // An auto cycle has no "To" — it counts up for as long as the cycle
        // runs — so requiring one would disable this button with no field on
        // screen to satisfy it.
        disabled={
          busy || !from || (!auto && (!to || Number(to) < Number(from)))
        }
        onClick={save}
      >
        Save new {auto ? 'cycle' : 'series'}
      </button>
    </Sheet>
  );
}
